// LandingLayout.jsx
//
// Source unique des espacements de la homepage. Avant cette harmonisation,
// chaque section (Hero / Interface / Features / CTA) définissait son propre
// padding de section, son propre conteneur "max-width", ses propres gaps —
// avec des valeurs proches mais jamais identiques (2.2rem / 2.4rem / 3.4rem,
// gaps à 0.56 / 0.62 / 0.64 / 0.72 / 0.84rem...). Résultat : un rythme
// vertical incohérent entre sections, et Features/CTA qui n'avaient même
// pas le même conteneur 1200px que Hero/Interface.
//
// Ce fichier centralise :
//   - une échelle d'espacement (landingSpacing.gap.*)
//   - le rythme vertical des sections (landingSpacing.section.*)
//   - le conteneur "max-width" partagé (SectionInner)
//   - les variantes de <section> (SectionOuter / HeroSectionOuter / CtaSectionOuter)
//   - un bloc de contenu centré réutilisé par Hero et CTA (CenteredContent)

import styled from 'styled-components';

export const landingSpacing = {
  gap: {
    xs: '0.5rem',   // 8px  – listes compactes, badges
    sm: '0.75rem',  // 12px – titre → sous-titre, groupes de boutons
    md: '1rem',     // 16px – padding de carte standard, gros titre → sous-titre
    lg: '1.5rem',   // 24px – bloc de contenu → bloc suivant
    xl: '2rem',     // 32px
    xxl: '3rem',    // 48px – séparation entre grands blocs (démos interface)
  },
  section: {
    paddingY: '3rem 0',
    paddingYMobile: '2.25rem 0',
    // Hero : un peu plus d'air en haut (juste sous le header), même base
    // qu'ailleurs en bas pour que la transition vers la section suivante
    // reste cohérente avec le reste de la page.
    paddingYHero: '3.5rem 0 3rem',
    paddingYHeroMobile: '2.5rem 0 2.25rem',
    // CTA : dernière section avant le footer, un peu plus d'air en bas.
    paddingYCta: '3rem 0 3.5rem',
    paddingYCtaMobile: '2.25rem 0 2.5rem',
  },
  container: {
    maxWidth: '1200px',
    contentMaxWidth: '760px',
    paddingX: '1.1rem',
    paddingXTablet: '0.85rem',
    paddingXMobile: '0.6rem',
  },
};

/* Conteneur pleine largeur commun à toutes les sections : centre le contenu
   à 1200px avec les mêmes points de rupture partout (1024px puis 360px). */
export const SectionInner = styled.div`
  max-width: ${landingSpacing.container.maxWidth};
  margin: 0 auto;
  padding: 0 ${landingSpacing.container.paddingX};
  box-sizing: border-box;

  @media (max-width: 1024px) {
    padding: 0 ${landingSpacing.container.paddingXTablet};
  }

  @media (max-width: 360px) {
    padding: 0 ${landingSpacing.container.paddingXMobile};
  }
`;

/* Section générique (Interface / Features) : même rythme vertical partout. */
export const SectionOuter = styled.section`
  padding: ${landingSpacing.section.paddingY};

  @media (max-width: 768px) {
    padding: ${landingSpacing.section.paddingYMobile};
  }
`;

/* Hero : cf. commentaire sur paddingYHero ci-dessus. */
export const HeroSectionOuter = styled.section`
  padding: ${landingSpacing.section.paddingYHero};

  @media (max-width: 768px) {
    padding: ${landingSpacing.section.paddingYHeroMobile};
  }
`;

/* CTA : cf. commentaire sur paddingYCta ci-dessus. */
export const CtaSectionOuter = styled.section`
  padding: ${landingSpacing.section.paddingYCta};

  @media (max-width: 768px) {
    padding: ${landingSpacing.section.paddingYCtaMobile};
  }
`;

/* Bloc de contenu centré, utilisé par Hero et CTA pour garder la même
   largeur de lecture sur les sections "texte + actions" qui encadrent
   la page. */
export const CenteredContent = styled.div`
  max-width: ${landingSpacing.container.contentMaxWidth};
  margin: 0 auto;
  text-align: center;
`;
